'use client';

import { Badge, Tooltip, Box } from '@mantine/core';
import { IconRosetteDiscountCheck, IconBook, IconUsers } from '@tabler/icons-react';

/**
 * VerificationBadge - Shows verification status of a set
 *
 * @param {boolean} verified - Whether the set is verified by admin
 * @param {string} source - 'automatic' or 'community'
 * @param {string} size - Badge size ('xs', 'sm', 'md', 'lg')
 * @param {boolean} showUnverified - Whether to show badge for unverified sets
 * @param {boolean} iconOnly - Show only the icon without text
 * @param {object} style - Additional styles
 */
export function VerificationBadge({
  verified,
  source,
  size = 'sm',
  showUnverified = false,
  iconOnly = false,
  style = {}
}) {
  const iconSize = size === 'lg' ? 18 : size === 'md' ? 16 : size === 'sm' ? 14 : 12;
  const iconOnlySize = size === 'lg' ? 15 : size === 'md' ? 13 : size === 'sm' ? 11 : 10;
  const iconOnlyPadding = size === 'lg' ? '5px 8px' : size === 'md' ? '4px 7px' : '3px 6px';

  if (verified) {
    if (iconOnly) {
      return (
        <Tooltip
          label="Verified by Bricks Per Bag"
          withArrow
          position="top"
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: iconOnlyPadding,
              borderRadius: '10px',
              backgroundColor: '#228be6',
              cursor: 'default',
              ...style
            }}
          >
            <IconRosetteDiscountCheck size={iconOnlySize} color="#fff" stroke={2} />
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        label="Verified by Bricks Per Bag"
        withArrow
        position="top"
      >
        <Badge
          variant="filled"
          size={size}
          radius="md"
          color="blue"
          leftSection={<IconRosetteDiscountCheck size={size === 'lg' ? 16 : size === 'md' ? 14 : 12} />}
          style={{
            cursor: 'default',
            ...style
          }}
        >
          Verified
        </Badge>
      </Tooltip>
    );
  }

  // Community-verified sets
  if (source === 'community') {
    if (iconOnly) {
      return (
        <Tooltip
          label="Verified with help from the community"
          withArrow
          position="top"
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: iconOnlyPadding,
              borderRadius: '10px',
              backgroundColor: '#099268',
              cursor: 'default',
              ...style
            }}
          >
            <IconUsers size={iconOnlySize} color="#fff" stroke={2} />
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        label="Verified with help from the community"
        withArrow
        position="top"
      >
        <Badge
          variant="filled"
          size={size}
          radius="md"
          color="teal"
          leftSection={<IconUsers size={size === 'lg' ? 16 : size === 'md' ? 14 : 12} />}
          style={{
            cursor: 'default',
            ...style
          }}
        >
          Community
        </Badge>
      </Tooltip>
    );
  }

  // Only show unverified badge if explicitly requested
  if (showUnverified) {
    if (iconOnly) {
      return (
        <Tooltip
          label="Bag contents come straight from LEGO's official building instructions. Part images are generated automatically, so a few prints may not show."
          withArrow
          position="top"
          multiline
          w={250}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: iconOnlyPadding,
              borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              cursor: 'default',
              ...style
            }}
          >
            <IconBook size={iconOnlySize} color="#868e96" stroke={2} />
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        label="Bag contents come straight from LEGO's official building instructions. Part images are generated automatically, so a few prints may not show."
        withArrow
        position="top"
        multiline
        w={250}
      >
        <Badge
          variant="light"
          size={size}
          radius="md"
          color="gray"
          leftSection={<IconBook size={size === 'lg' ? 16 : size === 'md' ? 14 : 12} />}
          style={{
            cursor: 'default',
            ...style
          }}
        >
          From LEGO instructions
        </Badge>
      </Tooltip>
    );
  }

  return null;
}

export default VerificationBadge;
